import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import { Checkbox, ErrorMessage, Input, CurrencyInput } from 'components/HookForm';
import InfoBanner from 'components/InfoBanner/InfoBanner';
import Message from 'components/Message';
import When from 'components/When';

import { ICONS } from 'constants/icons';
import { URL_SIMPLE_REGEX } from 'constants/logic';

import { bem } from 'helpers/class-name';

import ProofFileUpload from './ProofFileUpload/ProofFileUpload';
import { messages } from './ServicePricingSection.messages';

const { element } = bem('service-pricing-section');

const PricingInputs = ({
  venue,
  userId,
  locale,
  isManualStudio,
  prefillEnabled,
  proofFileUploadRef,
  servicePriceDescriptionMessage,
}) => {
  const { formState } = useFormContext();
  const { errors } = formState;

  const handleOpenPicker = () => proofFileUploadRef.current?.openPicker();

  return (
    <>
      <InfoBanner icon={ICONS.infoBrand} subtitle={messages.pricingInfoBanner} hideCTA />

      <CurrencyInput
        name="servicePrice"
        currencyCode={venue.currencyCode}
        locale={locale}
        label={messages.servicePrice}
        required
        showRequiredLabel
        noMargin
        data-cypress="service-price"
      />
      <Message as="p" message={servicePriceDescriptionMessage} className={element('input-description')} />

      <Input
        name="linkToPrice"
        type="text"
        label={messages.linkToPrice}
        labelTooltip={messages.linkToPriceTooltip}
        placeholder={messages.linkToPricePlaceholder}
        pattern={{
          value: URL_SIMPLE_REGEX,
          message: messages.invalidUrl,
        }}
        showRequiredLabel
        noMargin
        data-cypress="link-to-price"
      />
      {/* Link is optional at field level — a file can satisfy proof instead.
          Manual studios get the inline upload trigger; integrated studios just
          see the static helper text. */}
      {isManualStudio ? (
        <p className={element('input-description')}>
          <Message message={messages.linkToPriceDescription} /> <Message message={messages.noLinkUploadPrompt} />{' '}
          <button
            type="button"
            onClick={handleOpenPicker}
            className={element('upload-trigger')}
            data-cypress="proof-file-upload-trigger">
            <Message message={messages.uploadProofLink} />
          </button>
        </p>
      ) : (
        <Message as="p" message={messages.linkToPriceDescription} className={element('input-description')} />
      )}

      <When predicate={isManualStudio}>
        <ProofFileUpload
          ref={proofFileUploadRef}
          venueId={venue.venueId}
          userId={userId}
          prefillEnabled={prefillEnabled}
        />

        <div className="mt+">
          <Checkbox name="accountabilityAgreement" required={messages.accountabilityAgreementRequired}>
            <Message as="span" message={messages.accountabilityAgreementTitle} className="text--semibold" />
            <Message
              as="p"
              message={messages.accountabilityAgreementDescription}
              className={element('input-description')}
            />
          </Checkbox>
          {errors.accountabilityAgreement && <ErrorMessage name="accountabilityAgreement" errors={errors} />}
        </div>
      </When>
    </>
  );
};

PricingInputs.propTypes = {
  venue: PropTypes.shape({ venueId: PropTypes.number, currencyCode: PropTypes.string }).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locale: PropTypes.string,
  isManualStudio: PropTypes.bool,
  prefillEnabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  proofFileUploadRef: PropTypes.shape({ current: PropTypes.any }),
  servicePriceDescriptionMessage: PropTypes.shape({}),
};

export default PricingInputs;
